import {
  Card,
  Grid,
  Box,
  Skeleton,
  Alert,
  Divider,
  Menu,
  MenuItem,
  useMediaQuery,
  Tooltip as MuiTooltip,
} from "@mui/material";
import { useEffect, useMemo, useState } from "react";
import ByzMuiButton from "src/components/core-components/mui/buttons/ByzMuiButton";
import ByzMuiTypography from "src/components/core-components/mui/inputs/ByzMuiTypography";
import { useTranslation } from "react-i18next";
import { nurseService } from "src/api/nurse/nurseService";
import { axiosInstance } from "src/api/config/axiosInstance";
import {
  LineChart,
  XAxis,
  YAxis,
  Tooltip,
  Line,
  ResponsiveContainer,
  CartesianGrid,
} from "recharts";
import { useSelector } from "react-redux";

type PatientDataProps = {
  isCreateMedCard?: boolean;
  isOpenMedCard?: boolean;
  patientId?: any;
  noMargin?: boolean;
  onlyVitalsSection?: boolean;
  patientDataOut?: (data: any) => void;
  cardRows?: any[];
  cardData?: any;
  orId?: number;
  onLoadingChange?: (loading: boolean) => void;
  showBloodPressureChart?: boolean;
  showDailyPulseChart?: boolean;
};

type StabilityVariant = "good" | "moderate" | "severe" | "critical";

function PatientDatas({
  isCreateMedCard = false,
  isOpenMedCard = false,
  patientId,
  noMargin = false,
  onlyVitalsSection = false,
  patientDataOut,
  cardRows,
  cardData,
  orId,
  onLoadingChange,
  showBloodPressureChart = true,
  showDailyPulseChart = true,
}: PatientDataProps) {
  const isNarrowScreen = useMediaQuery("(max-width: 1200px)");
  const useMobileLayout = isNarrowScreen;
  const [isolationTypeIconsData, setIsolationTypeIconsData] = useState<any[] | null>(null);
  const [patientData, setPatientData] = useState<any>(null);
  const [vitalRecords, setVitalRecords] = useState<any[]>([]);
  const { t } = useTranslation();
  const [minimized, setMinimized] = useState(true);

  type VitalFilterOption =
    | "last5"
    | "today"
    | "last24h"
    | "last3d"
    | "last5d"
    | "last7d"
    | "all";

  const [vitalFilter, setVitalFilter] = useState<VitalFilterOption>("last5");
  const [scoreFilter, setScoreFilter] = useState<VitalFilterOption>("last5");
  const [vitalFilterAnchorEl, setVitalFilterAnchorEl] = useState<null | HTMLElement>(null);
  const [scoreFilterAnchorEl, setScoreFilterAnchorEl] = useState<null | HTMLElement>(null);
  const isVitalFilterMenuOpen = Boolean(vitalFilterAnchorEl);
  const isScoreFilterMenuOpen = Boolean(scoreFilterAnchorEl);
  const ISOLATION_ICONS_CACHE_KEYS = ["isolationTypeIconsCache", "IsolationTypeIconsCache", "IsolationTypeIcon"];
  const ONE_DAY_MS = 24 * 60 * 60 * 1000;

  useEffect(() => {
    const cached = ISOLATION_ICONS_CACHE_KEYS
      .map((key) => localStorage.getItem(key))
      .find((value) => Boolean(value));

    if (cached) {
      try {
        const parsed = JSON.parse(cached);
        if (parsed?.ts && parsed?.data && Date.now() - parsed.ts < ONE_DAY_MS) {
          setIsolationTypeIconsData(parsed.data);
          return;
        }
      } catch (err) {
        console.warn("Isolation icon cache parse error:", err);
      }
    }

    const fetchIcons = async () => {
      try {
        const res = await axiosInstance.get("isolation/api/IsolationType/GetIsolationTypeIcons");
        const iconData = res?.data?.data ?? res?.data ?? [];
        setIsolationTypeIconsData(iconData);
        localStorage.setItem(
          ISOLATION_ICONS_CACHE_KEYS[0],
          JSON.stringify({ ts: Date.now(), data: iconData })
        );
      } catch (err) {
        console.error("Isolation icon fetch error:", err);
      }
    };

    fetchIcons();
  }, []);

  const isolationIconsMap = useMemo(() => {
    const arr: any[] = Array.isArray(isolationTypeIconsData) ? isolationTypeIconsData : [];
    const map: Record<number, string> = {};
    arr.forEach((icon: any) => {
      const id = Number(icon?.isolationtypeid ?? icon?.IsolationTypeId ?? icon?.id ?? icon?.ID);
      const svg = icon?.pics_svg ?? icon?.PICS_SVG;
      if (id && svg) map[id] = svg;
    });
    return map;
  }, [isolationTypeIconsData]);

  const filterLabelMap: Record<VitalFilterOption, string> = {
    last5: "Son 5 Kayıt",
    today: "Bugün",
    last24h: "24 Saat",
    last3d: "3 Gün",
    last5d: "5 Gün",
    last7d: "7 Gün",
    all: "Tümü",
  };

  const vitalFilterLabel = filterLabelMap[vitalFilter];
  const scoreFilterLabel = filterLabelMap[scoreFilter];

  const vitalTitleRaw = t("patient-data.vital-information");
  const vitalTitle =
    vitalTitleRaw === "patient-data.vital-information"
      ? "Hasta Vital Bilgileri"
      : vitalTitleRaw;
  const resolvedVitalTitle = onlyVitalsSection ? "Vital Bilgiler" : vitalTitle;

  const scoresTitleRaw = t("patient-data.scores");
  const scoresTitle =
    scoresTitleRaw === "patient-data.scores" ? "Skorlar" : scoresTitleRaw;


  const calculateAge = (birthDate: string) => {
    if (!birthDate) return "-";

    const birth = new Date(birthDate);
    const today = new Date();

    let years = today.getFullYear() - birth.getFullYear();
    let months = today.getMonth() - birth.getMonth();
    let days = today.getDate() - birth.getDate();

    if (days < 0) {
      months--;
      const lastMonth = new Date(today.getFullYear(), today.getMonth(), 0);
      days += lastMonth.getDate();
    }

    if (months < 0) {
      years--;
      months += 12;
    }

    return `${years} yıl ${months} ay ${days} gün`;
  };

  const chartScoreLabel = useMemo(() => {
    if (!cardData?.chartScoreField) {
      return t("score");
    }

    const scoreRowConfig = cardData.rows?.find((r: any) => r.value === cardData.chartScoreField);
    if (scoreRowConfig) {
      const scoreFieldConfig = cardData.fields?.find((f: any) => f.field === scoreRowConfig.title);
      if (scoreFieldConfig?.headerName) {
        return t(scoreFieldConfig.headerName);
      }
    }

    const directFieldConfig = cardData.fields?.find((f: any) => f.field === cardData.chartScoreField);
    if (directFieldConfig?.headerName) {
      return t(directFieldConfig.headerName);
    }

    return cardData.chartScoreField;
  }, [cardData, t]);

  const mainTabs = useSelector((state: any) => state.tabs.mainTabs);
  const activeTabIndex = useSelector((state: any) => state.tabs.activeTabIndex);
  const activeTab = mainTabs[activeTabIndex];
  const tabPatientId = activeTab?.data?.patientId;

  const derivedPatientId = patientId || tabPatientId;

  useEffect(() => {
    const fetchPatientData = async () => {
      if (onLoadingChange) onLoadingChange(true);
      setPatientData(null);
      try {
        const [patientInfo, vitals] = await Promise.all([
          nurseService.patient.getPatientInfo<any>({ id: Number(derivedPatientId) }),
          nurseService.patient.getPatientVitalRecords<{ data: any[] }>({
            ptId: Number(derivedPatientId),
            filterType: "All",
          }),
        ]);
        setPatientData(patientInfo);
        setVitalRecords(vitals?.data || []);
      } catch (error) {
        console.error("âŒ Hasta bilgisi alÄ±namadÄ± veya vital kayÄ±tlar getirilemedi:", error);
        setPatientData(undefined);
        setVitalRecords([]);
      } finally {
        if (onLoadingChange) onLoadingChange(false);
      }
    };

    if (derivedPatientId) {
      fetchPatientData();
    } else {
      console.warn("â›” ID bulunamadı, istek atÄ±lmayacak.");
      if (onLoadingChange) onLoadingChange(false);
      setVitalRecords([]);
    }
  }, [derivedPatientId]);

  useEffect(() => {
    if (patientDataOut && patientData) {
      patientDataOut(patientData);
    }
    // patientDataOut intentionally omitted: including it causes infinite re-renders
    // when the parent passes an unstable (inline) function reference
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [patientData]);

  const data = patientData?.data;

  const sortedVitalRecordsAll = useMemo(() => {
    return [...vitalRecords].sort((a, b) => {
      const dateA = new Date(a.ctime || 0).getTime();
      const dateB = new Date(b.ctime || 0).getTime();
      return dateB - dateA;
    });
  }, [vitalRecords]);

  const filteredVitalRecords = useMemo(() => {
    const now = new Date();
    const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
    const nowMs = now.getTime();

    const byTimeRange = (ms: number) =>
      sortedVitalRecordsAll.filter((r) => {
        const t = new Date(r.ctime || 0).getTime();
        return Number.isFinite(t) && t >= ms && t <= nowMs;
      });

    switch (vitalFilter) {
      case "last5":
        return sortedVitalRecordsAll.slice(0, 5);
      case "today":
        return byTimeRange(startOfToday);
      case "last24h":
        return byTimeRange(nowMs - 24 * 60 * 60 * 1000);
      case "last3d":
        return byTimeRange(nowMs - 3 * 24 * 60 * 60 * 1000);
      case "last5d":
        return byTimeRange(nowMs - 5 * 24 * 60 * 60 * 1000);
      case "last7d":
        return byTimeRange(nowMs - 7 * 24 * 60 * 60 * 1000);
      case "all":
      default:
        return sortedVitalRecordsAll;
    }
  }, [sortedVitalRecordsAll, vitalFilter]);

  const sortedVitalRecords = [...filteredVitalRecords].slice(0, 200);

  const formatChartDate = (dateString: string): string => {
    const date = new Date(dateString);
    const currentYear = new Date().getFullYear();
    const recordYear = date.getFullYear();

    const twoDigit = (value: number) => value.toString().padStart(2, "0");
    const day = twoDigit(date.getDate());
    const month = twoDigit(date.getMonth() + 1);
    const time = `${twoDigit(date.getHours())}:${twoDigit(date.getMinutes())}`;

    if (recordYear === currentYear) {
      return `${day}.${month} ${time}`;
    }

    return `${day}.${month}.${recordYear} ${time}`;
  };

  const prepareChartData = () => {
    const sysData: (number | null)[] = [];
    const diaData: (number | null)[] = [];
    const pulseData: (number | null)[] = [];
    const dates: string[] = [];

    sortedVitalRecords.forEach((record) => {
      sysData.push(
        record.sys !== undefined && record.sys !== null
          ? Number(record.sys)
          : null
      );
      diaData.push(
        record.dia !== undefined && record.dia !== null
          ? Number(record.dia)
          : null
      );
      pulseData.push(
        record.heartrate !== undefined && record.heartrate !== null
          ? Number(record.heartrate)
          : null
      );

      if (record.ctime) {
        dates.push(formatChartDate(record.ctime));
      } else {
        dates.push("Kayıt yok");
      }
    });

    return {
      sys: sysData.reverse(),
      dia: diaData.reverse(),
      pulse: pulseData.reverse(),
      dates: dates.reverse(),
    };
  };

  const chartValues = prepareChartData();

  const filteredScoreRows = useMemo(() => {
    if (!cardRows || !cardData?.chartTimeField) return cardRows;
    if (scoreFilter === "all" || scoreFilter === "last5") return cardRows;

    const now = new Date();
    const nowMs = now.getTime();
    const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();

    const getStartMs = () => {
      switch (scoreFilter) {
        case "today":
          return startOfToday;
        case "last24h":
          return nowMs - 24 * 60 * 60 * 1000;
        case "last3d":
          return nowMs - 3 * 24 * 60 * 60 * 1000;
        case "last5d":
          return nowMs - 5 * 24 * 60 * 60 * 1000;
        case "last7d":
          return nowMs - 7 * 24 * 60 * 60 * 1000;
        default:
          return undefined;
      }
    };

    const startMs = getStartMs();
    if (!startMs) return cardRows;

    const timeField = cardData.chartTimeField;
    return cardRows.filter((row) => {
      const t = new Date(row?.[timeField] || 0).getTime();
      return Number.isFinite(t) && t >= startMs && t <= nowMs;
    });
  }, [cardRows, cardData, scoreFilter]);

  const prepareScoreData = () => {
    const sourceRows = filteredScoreRows || cardRows;
    if (!sourceRows || !cardData || !cardData.chartScoreField || !cardData.chartTimeField) {
      return null;
    }

    const scoreField = cardData.chartScoreField;
    const timeField = cardData.chartTimeField;

    const sortedRows = [...sourceRows]
      .filter((row) => {
        let scoreValue = row[scoreField];

        if (scoreValue === null || scoreValue === undefined || scoreValue === "") {
          const scoreRowConfig = cardData.rows?.find((r: any) => r.value === scoreField);
          const scoreFieldConfig = scoreRowConfig
            ? cardData.fields?.find((f: any) => f.field === scoreRowConfig.title)
            : cardData.fields?.find((f: any) => f.field === scoreField);

          if (scoreFieldConfig?.computed && scoreFieldConfig?.calculate) {
            scoreValue = scoreFieldConfig.calculate(row);
          }
        }

        return scoreValue !== null && scoreValue !== undefined && scoreValue !== "";
      })
      .sort((a, b) => {
        const dateA = new Date(a[timeField] || 0).getTime();
        const dateB = new Date(b[timeField] || 0).getTime();
        return dateB - dateA;
      })
      .slice(0, scoreFilter === "last5" ? 5 : 200);

    const scoreData: (number | null)[] = [];
    const scoreDates: string[] = [];

    sortedRows.forEach((row, index) => {
      let scoreValue = row[scoreField];

      if (scoreValue === null || scoreValue === undefined || scoreValue === "") {
        const scoreRowConfig = cardData.rows?.find((r: any) => r.value === scoreField);
        const scoreFieldConfig = scoreRowConfig
          ? cardData.fields?.find((f: any) => f.field === scoreRowConfig.title)
          : cardData.fields?.find((f: any) => f.field === scoreField);

        if (scoreFieldConfig?.computed && scoreFieldConfig?.calculate) {
          scoreValue = scoreFieldConfig.calculate(row);
        }
      }

      scoreData.push(
        scoreValue !== null && scoreValue !== undefined
          ? Number(scoreValue)
          : null
      );

      if (row[timeField]) {
        scoreDates.push(formatChartDate(row[timeField]));
      } else {
        scoreDates.push("Kayıt yok");
      }
    });

    const result = {
      scores: scoreData.reverse(),
      dates: scoreDates.reverse(),
    };

    return result;
  };

  const scoreData = prepareScoreData();

  const calculateMinMax = (values: (number | null)[]) => {
    const validValues = values.filter(
      (v): v is number => v !== null && v !== undefined && !Number.isNaN(v)
    );
    if (validValues.length === 0) {
      return { min: 0, max: 100 };
    }
    const min = Math.min(...validValues);
    const max = Math.max(...validValues);
    if (min === 0 && max === 0) {
      return { min: 0, max: 100 };
    }
    if (min === max) {
      return {
        min: Math.max(0, Math.floor(min)),
        max: Math.ceil(max + 10),
      };
    }
    return {
      min: Math.max(0, Math.floor(min)),
      max: Math.ceil(max),
    };
  };

  const sysMinMax = calculateMinMax(chartValues.sys);
  const diaMinMax = calculateMinMax(chartValues.dia);
  const pulseMinMax = calculateMinMax(chartValues.pulse);
  const scoreMinMax = scoreData
    ? calculateMinMax(scoreData.scores)
    : { min: 0, max: 100 };

  const normalizeValue = (value: number, min: number, max: number) => {
    if (value === undefined || value === null || Number.isNaN(value)) {
      return 0;
    }
    if (max === min) return 0.5;
    const normalized = (value - min) / (max - min);
    return Math.max(0, Math.min(1, normalized));
  };

  const normalizeSysValue = (value?: number) => normalizeValue(value || 0, sysMinMax.min, sysMinMax.max);
  const normalizeDiaValue = (value?: number) => normalizeValue(value || 0, diaMinMax.min, diaMinMax.max);
  const normalizePulseValue = (value?: number) => normalizeValue(value || 0, pulseMinMax.min, pulseMinMax.max);
  const normalizeScoreValue = (value?: number) => normalizeValue(value || 0, scoreMinMax.min, scoreMinMax.max);

  const bloodPressureData = chartValues.sys.map((sysVal, index) => {
    const dateLabel = chartValues.dates[index] || "Kayıt yok";
    return {
      name: dateLabel,
      dateLabel,
      SYS: sysVal,
      DIA: chartValues.dia[index] ?? null,
    };
  });

  const bloodPressureDataNormalized = bloodPressureData.map((item) => ({
    ...item,
    SYSNormalized:
      item.SYS === null || item.SYS === undefined
        ? null
        : normalizeSysValue(item.SYS),
    DIANormalized:
      item.DIA === null || item.DIA === undefined
        ? null
        : normalizeDiaValue(item.DIA),
  }));

  const pulseChartData = chartValues.pulse.map((pulseVal, index) => {
    const dateLabel = chartValues.dates[index] || "Kayıt yok";
    return {
      name: dateLabel,
      dateLabel,
      Pulse: pulseVal,
    };
  });

  const pulseDataNormalized = pulseChartData.map((item) => ({
    ...item,
    PulseNormalized:
      item.Pulse === null || item.Pulse === undefined
        ? null
        : normalizePulseValue(item.Pulse),
  }));

  const chartVitalRecordsAsc = useMemo(() => {
    return [...filteredVitalRecords]
      .filter((r) => r?.ctime)
      .sort((a, b) => new Date(a.ctime).getTime() - new Date(b.ctime).getTime())
      .slice(-200);
  }, [filteredVitalRecords]);

  const vitalChartData = useMemo(() => chartVitalRecordsAsc.map((record) => ({
    name: record.ctime ? formatChartDate(record.ctime) : "Kayıt yok",
    dateLabel: record.ctime ? formatChartDate(record.ctime) : "Kayıt yok",
    Pulse:
      record.heartrate !== undefined && record.heartrate !== null
        ? Number(record.heartrate)
        : null,
    SYS:
      record.sys !== undefined && record.sys !== null
        ? Number(record.sys)
        : null,
    DIA:
      record.dia !== undefined && record.dia !== null
        ? Number(record.dia)
        : null,
    SpO2:
      record.spo2 !== undefined && record.spo2 !== null
        ? Number(record.spo2)
        : null,
  })), [chartVitalRecordsAsc]);

  const visibleVitalChartData = useMemo(
    () => (useMobileLayout ? vitalChartData.slice(-5) : vitalChartData),
    [useMobileLayout, vitalChartData]
  );

  const vitalAllValues: (number | null)[] = [
    ...visibleVitalChartData.map((d) => d.Pulse),
    ...visibleVitalChartData.map((d) => d.SYS),
    ...visibleVitalChartData.map((d) => d.DIA),
    ...visibleVitalChartData.map((d) => d.SpO2),
  ];
  const vitalMinMax = calculateMinMax(vitalAllValues);

  const scoreChartData = scoreData
    ? scoreData.scores.map((scoreVal, index) => {
      const dateLabel = scoreData.dates[index] || "Kayıt yok";
      return {
        name: dateLabel,
        dateLabel,
        Score: scoreVal,
      };
    })
    : [];

  const scoreChartDataNormalized = scoreChartData.map((item) => ({
    ...item,
    ScoreNormalized:
      item.Score === null || item.Score === undefined
        ? null
        : normalizeScoreValue(item.Score),
  }));
  const hasScoreChart = Boolean(cardData?.chartScoreField);
  const chartGridTemplateColumns = useMemo(() => {
    if (onlyVitalsSection) {
      return hasScoreChart ? { xs: "1fr", md: "minmax(0, 1fr) minmax(0, 1fr)" } : "1fr";
    }

    return hasScoreChart
      ? { xs: "1fr", md: "minmax(0, 11.5fr) minmax(0, 11.5fr) minmax(40px, 1fr)" }
      : { xs: "1fr", md: "minmax(0, 23fr) minmax(40px, 1fr)" };
  }, [hasScoreChart, onlyVitalsSection]);

  const getChartTickAngle = (pointCount: number) => {
    if (pointCount <= 5) return 0;
    if (pointCount <= 8) return -25;
    if (pointCount <= 12) return -45;
    if (pointCount <= 16) return -60;
    return -90;
  };

  const getChartBottomMargin = (angle: number) => {
    if (angle === 0) return 13;
    if (Math.abs(angle) <= 25) return 22;
    if (Math.abs(angle) <= 45) return 30;
    if (Math.abs(angle) <= 60) return 38;
    return 48;
  };

  const getChartTickHeight = (angle: number) => {
    if (angle === 0) return 30;
    if (Math.abs(angle) <= 25) return 36;
    if (Math.abs(angle) <= 45) return 44;
    if (Math.abs(angle) <= 60) return 52;
    return 60;
  };

  const vitalTickAngle = useMobileLayout ? -90 : getChartTickAngle(visibleVitalChartData.length);
  const scoreTickAngle = getChartTickAngle(scoreChartDataNormalized.length);
  const vitalChartBottomMargin = useMobileLayout ? 10 : getChartBottomMargin(vitalTickAngle);
  const scoreChartBottomMargin = getChartBottomMargin(scoreTickAngle);
  const vitalChartTickHeight = useMobileLayout ? 54 : getChartTickHeight(vitalTickAngle);
  const scoreChartTickHeight = getChartTickHeight(scoreTickAngle);

  const tooltipDataKeyMap: Record<string, string> = {
    SYSNormalized: "SYS",
    DIANormalized: "DIA",
    PulseNormalized: "Pulse",
    ScoreNormalized: "Score",
  };

  const chartTooltipFormatter = (
    value: number,
    name: string,
    props: any
  ): [number, string] => {
    const originalKey = tooltipDataKeyMap[props.dataKey as string];
    const originalValue =
      originalKey !== undefined ? props.payload?.[originalKey] : value;
    return [originalValue ?? value, name];
  };

  const chartTooltipLabelFormatter = (label: string, payload: any[]): string => {
    if (payload && payload.length > 0 && payload[0]?.payload?.dateLabel) {
      return payload[0].payload.dateLabel;
    }
    return label || "";
  };

  const [isVitalsOpen, setIsVitalsOpen] = useState(onlyVitalsSection);

  if (patientData === null) {
    return (
      <Card sx={{ p: 3, mb: 2 }}>
        <Grid container spacing={2}>
          <Grid item xs={12} md={3}>
            <Box display="flex" alignItems="center" gap={2}>
              <Skeleton variant="circular" width={60} height={60} />
              <Box flex={1}>
                <Skeleton variant="text" width="80%" height={24} />
                <Skeleton variant="text" width="60%" height={20} />
                <Skeleton variant="text" width="70%" height={20} />
              </Box>
            </Box>
          </Grid>
          <Grid item xs={12} md={9}>
            <Grid container spacing={2}>
              {[...Array(6)].map((_, index) => (
                <Grid item xs={12} sm={6} md={4} key={index}>
                  <Skeleton variant="text" width="100%" height={20} />
                </Grid>
              ))}
            </Grid>
          </Grid>
        </Grid>
      </Card>
    );
  }

  if (patientData === undefined || !data) {
    return (
      <Card sx={{ p: 3, mb: 2 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          Hasta bilgisi bulunamadı veya yüklenirken bir hata oluştu.
        </Alert>
      </Card>
    );
  }

  const fullName = `${data.name || ""} ${data.surname || ""}`;
  const protocolText = `${data.upn || "-"} / ${data.protid || "-"}`;
  const gender = data.sexuality || "-";
  const genderUpper = gender.toUpperCase();
  const isFemale =
    genderUpper === "KADIN" ||
    genderUpper === "K" ||
    genderUpper === "FEMALE" ||
    genderUpper === "F";
  const avatarIcon = isFemale
    ? "/assets/icons/pages/nurse/patient-data/icons/user-woman-icon.svg"
    : "/assets/icons/pages/nurse/patient-data/icons/user.svg";
  const doctor = data.drname || data.refdrname || "-";
  const deptName = data.deptname || data.hrname || "-";
  const bedInfo = data.patbedinfo || "-";
  const bloodGroup = data.bloodgroup || "-";
  const hasBloodGroup = bloodGroup !== "-";
  const diagnosis = data.diseasecodes || data.diagnosis || data.tani || "-";
  const ageText = data.age || calculateAge(data.birthdate);
  const visitTypeName = data.visittypename || data.visittypeName || data.visittype || "-";
  const admissionDateRaw = data.ptctime || data.mprctime || data.ctime || "-";

  const vitalSignStatusRaw = data.vitalsignstatus || data.vitalSignStatus || "-";
  const vitalSignStatusMap: Record<string, string> = {
    Good: "İyi",
    Medium: "Orta",
    Severe: "Ağır",
    Critical: "Kritik",
  };
  const vitalSignStatus = vitalSignStatusMap[vitalSignStatusRaw] ?? vitalSignStatusRaw;
  const mapVitalSignStatus = (raw: any): { variant: StabilityVariant; label: string } | null => {
    if (raw === null || raw === undefined || raw === "") return null;

    const numericValue = Number(raw);
    if (!Number.isNaN(numericValue)) {
      if (numericValue === 0) return { variant: "good", label: "İyi" };
      if (numericValue === 1) return { variant: "moderate", label: "Orta" };
      if (numericValue === 2) return { variant: "severe", label: "Ağır" };
      if (numericValue === 3) return { variant: "critical", label: "Kritik" };
    }

    const value = String(raw).trim().toLowerCase();
    if (value === "good" || value === "iyi") return { variant: "good", label: "İyi" };
    if (value === "medium" || value === "moderate" || value === "orta") return { variant: "moderate", label: "Orta" };
    if (value === "severe" || value === "ağır" || value === "agir") return { variant: "severe", label: "Ağır" };
    if (value === "critical" || value === "kritik") return { variant: "critical", label: "Kritik" };

    return null;
  };
  const normalizedVitalSignStatus = mapVitalSignStatus(vitalSignStatusRaw ?? vitalSignStatus);
  const displayVitalSignStatus = normalizedVitalSignStatus?.label ?? vitalSignStatus;
  const stabilityBadgePalette = normalizedVitalSignStatus
    ? {
        good: { bg: "#ecfdf5", border: "#a7f3d0", fg: "#047857" },
        moderate: { bg: "#fffbeb", border: "#fcd34d", fg: "#b45309" },
        severe: { bg: "#fff7ed", border: "#fdba74", fg: "#c2410c" },
        critical: { bg: "#fef2f2", border: "#fecaca", fg: "#b91c1c" },
      }[normalizedVitalSignStatus.variant]
    : { bg: "#f8fafc", border: "#cbd5e1", fg: "#475569" };

  const formatDateShort = (dateString: string) => {
    const d = new Date(dateString);
    if (Number.isNaN(d.getTime())) return "-";
    const dd = String(d.getDate()).padStart(2, "0");
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const yyyy = d.getFullYear();
    return `${dd}.${mm}.${yyyy}`;
  };

  const bmi =
    data.weight && data.patlength
      ? (Number(data.weight) / Math.pow(Number(data.patlength) / 100, 2)).toFixed(1)
      : "-";
  const heightWeightBmiText = `${data.patlength || "-"} / ${data.weight || "-"} / ${bmi}`;
  const admissionDateText = admissionDateRaw !== "-" ? formatDateShort(admissionDateRaw) : "-";

  const iconBlueFilter =
    "brightness(0) saturate(100%) invert(26%) sepia(97%) saturate(2225%) hue-rotate(219deg) brightness(93%) contrast(101%)";
  const isolationIds = Array.isArray(data.isolationtypeids) ? data.isolationtypeids : [];

  const renderIsolationIconsRow = (size = 20, iconSize = 14) => {
    if (!isolationIds.length) return null;

    return (
      <Box
        sx={{
          mt: 0.5,
          display: "flex",
          alignItems: "center",
          gap: 0.5,
          flexWrap: "wrap",
        }}
      >
        {isolationIds.map((id: any) => {
          const svg = isolationIconsMap[Number(id)];
          if (!svg) return null;
          return (
            <Box
              key={`pd-iso-${id}`}
              sx={{
                width: size,
                height: size,
                borderRadius: "999px",
                border: "1px solid #fff",
                background:
                  "linear-gradient(0deg, rgba(195, 202, 217, 0.55) 0%, rgba(195, 202, 217, 0.55) 100%), #FFF",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <Box
                component="img"
                src={`data:image/svg+xml;base64,${svg}`}
                alt=""
                sx={{ width: iconSize, height: iconSize }}
              />
            </Box>
          );
        })}
      </Box>
    );
  };

  const summaryCards = [
    {
      icon: "/assets/icons/pages/nurse/patient-data/icons/order.svg",
      label: "Dosya - Protokol",
      value: protocolText,
      span: 3,
    },
    {
      icon: "/assets/icons/pages/nurse/patient-data/icons/hospital-bed.svg",
      label: "Oda / Yatak",
      value: bedInfo,
      span: 3,
    },
    {
      icon: "/assets/icons/pages/nurse/patient-data/icons/department.svg",
      label: "Bölüm",
      value: deptName,
      span: 3,
    },
    {
      label: "Geliş Nedeni",
      value: visitTypeName || diagnosis,
      span: 2,
    },
    {
      label: "Stabilite",
      value: displayVitalSignStatus,
      badge: true as const,
      span: 2,
    },
  ];

  const detailCards = [
    {
      icon: "/assets/icons/pages/nurse/patient-data/icons/height-weight-vki.svg",
      label: "Boy / Kilo / VKI",
      value: heightWeightBmiText,
      iconSize: 20,
      span: 3,
    },
    {
      icon: "/assets/icons/pages/nurse/patient-data/icons/patient-clock.svg",
      label: "Yatış Zamanı",
      value: admissionDateText,
      iconSize: 17,
      span: 4,
    },
    {
      icon: "/assets/icons/pages/nurse/patient-data/icons/doctor.svg",
      label: "Doktor",
      value: doctor,
      iconSize: 20,
      span: 3,
    },
    {
      icon: "/assets/icons/pages/nurse/patient-data/icons/diagnosis.svg",
      label: "Tanı",
      value: diagnosis,
      iconSize: 20,
      span: 4,
    },
  ];

  const renderMobileInfoCard = (item: any) => (
    <Box
      key={`${item.label}-${item.value}`}
      sx={{
        gridColumn: `span ${item.span || 2}`,
        borderRadius: "14px",
        bgcolor: "#fff",
        boxShadow: "0px 8px 24px rgba(67, 71, 77, 0.08)",
        px: 1.25,
        py: 1,
        minHeight: 56,
        display: "flex",
        alignItems: "center",
        gap: 1,
      }}
    >
      {item.icon && (
        <Box
          component="img"
          src={item.icon}
          alt=""
          sx={{
            width: item.iconSize || 16,
            height: item.iconSize || 16,
            flexShrink: 0,
            filter: iconBlueFilter,
          }}
        />
      )}
      <Box sx={{ minWidth: 0, width: "100%" }}>
        <Box
          sx={{
            color: "#838fa0",
            fontSize: 9,
            lineHeight: "14px",
            letterSpacing: "-0.01em",
          }}
        >
          {item.label}
        </Box>
        {item.badge ? (
          <Box
            sx={{
              mt: 0.5,
              width: "fit-content",
              minWidth: 46,
              borderRadius: "8px",
              bgcolor: stabilityBadgePalette.bg,
              color: stabilityBadgePalette.fg,
              border: "1px solid",
              borderColor: stabilityBadgePalette.border,
              px: 1,
              py: 0.25,
              fontSize: 10,
              lineHeight: "15px",
              fontWeight: 600,
              textAlign: "center",
            }}
          >
            {item.value}
          </Box>
        ) : (
          <MuiTooltip
            title={String(item.value ?? "-")}
            arrow
            enterTouchDelay={0}
            leaveTouchDelay={2500}
          >
            <Box
              sx={{
                color: "#2c4364",
                fontSize: 12,
                lineHeight: "18px",
                fontWeight: 600,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {item.value}
            </Box>
          </MuiTooltip>
        )}
      </Box>
    </Box>
  );

  return (
    <>
      {!onlyVitalsSection && (
      <>
      {/* Patient Information Card */}
      {minimized ? (
        useMobileLayout ? (
          <Card
            sx={{
              p: 0,
              mb: noMargin ? 0 : 2,
              borderRadius: 2,
              border: "1px solid",
              borderColor: "divider",
              overflow: "hidden",
              width: "100%",
            }}
          >
            <Box sx={{ p: 1.5, borderBottom: "1px solid #f1f5f9" }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.25 }}>
                <Box
                  sx={{
                    width: 42,
                    height: 42,
                    borderRadius: "999px",
                    bgcolor: "#f1f5f9",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                  }}
                >
                  <Box
                    component="img"
                    src={avatarIcon}
                    alt=""
                    sx={{ width: 34, height: 34, borderRadius: "999px", objectFit: "cover" }}
                  />
                </Box>
                <Box sx={{ minWidth: 0, flex: 1, display: "flex", flexDirection: "column", justifyContent: "center" }}>
                  <Box
                    component="b"
                    sx={{
                      display: "block",
                      color: "#0f172a",
                      fontSize: 16,
                      lineHeight: "22px",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {fullName}
                  </Box>
                  <Box sx={{ color: "#64748b", fontSize: 11, lineHeight: "16px", fontWeight: 600, mt: 0.25 }}>
                    <Box component="span" sx={{ display: "block" }}>
                      {ageText}
                    </Box>
                    {hasBloodGroup && (
                      <Box component="span" sx={{ display: "block" }}>
                        {bloodGroup}
                      </Box>
                    )}
                  </Box>
                  {renderIsolationIconsRow(18, 12)}
                </Box>
              </Box>

              <Box
                sx={{
                  mt: 1.25,
                  display: "grid",
                  gridTemplateColumns: "repeat(7, minmax(0, 1fr))",
                  gap: 1,
                  width: "100%",
                }}
              >
                {summaryCards.slice(0, 2).map(renderMobileInfoCard)}
                <Box
                  sx={{
                    gridColumn: "span 1",
                    minHeight: 56,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <ByzMuiButton
                    variant2="text"
                    onClick={() => setMinimized(false)}
                    sx={{
                      minWidth: 28,
                      width: 28,
                      height: 28,
                      p: 0,
                      backgroundColor: "transparent",
                      "&:hover": { backgroundColor: "transparent", opacity: 0.8 },
                    }}
                  >
                    <Box component="img" src="/assets/icons/vector/down.svg" alt="" sx={{ width: 24, height: 24 }} />
                  </ByzMuiButton>
                </Box>
                {summaryCards.slice(2).map(renderMobileInfoCard)}
              </Box>
            </Box>
          </Card>
        ) : (
        <Card
          sx={{
            p: 0,
            mb: noMargin ? 0 : 2,
            borderRadius: 2,
            border: "1px solid",
            borderColor: "divider",
            overflow: "hidden",
            width: "100%",
          }}
        >
          <Box
            sx={{
              width: "100%",
              borderBottom: "1px solid #f1f5f9",
              boxSizing: "border-box",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              padding: "10px 12px",
              textAlign: "center",
              fontSize: 14,
              color: "#64748b",
              fontFamily: "Poppins",
            }}
          >
            <Box
              sx={{
                width: "100%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: "10px",
                maxWidth: "100%",
              }}
            >
              <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center" }}>
                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: "10px" }}>
                  <Box component="img" src={avatarIcon} alt="" sx={{ width: 50, height: 50, borderRadius: "999px", objectFit: "cover" }} />
                  <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
                    <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: "5px" }}>
                      <Box
                        component="b"
                        sx={{
                          width: 143,
                          height: 30,
                          fontSize: 20,
                          lineHeight: "36px",
                          display: "inline-block",
                          color: "#0f172a",
                          textAlign: "left",
                          flexShrink: 0,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {fullName}
                      </Box>
                      <Box sx={{ alignSelf: "stretch", lineHeight: "24px", fontWeight: 600 }}>
                        <Box sx={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                          {ageText}
                        </Box>
                        {hasBloodGroup && (
                          <Box sx={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                            {bloodGroup}
                          </Box>
                        )}
                      </Box>
                      {renderIsolationIconsRow(20, 14)}
                    </Box>
                  </Box>
                </Box>
              </Box>

              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "flex-start",
                  justifyContent: "center",
                  textAlign: "left",
                  fontSize: 10,
                  color: "#838fa0",
                }}
              >
                <Box sx={{ display: "flex", alignItems: "flex-start", gap: "10px" }}>
                  {[
                    {
                      icon: "/assets/icons/pages/nurse/patient-data/icons/order.svg",
                      label: "Dosya - Protokol",
                      value: protocolText,
                      w: 176,
                    },
                    {
                      icon: "/assets/icons/pages/nurse/patient-data/icons/hospital-bed.svg",
                      label: "ODA NO",
                      value: bedInfo,
                      w: 176,
                    },
                    {
                      icon: "/assets/icons/pages/nurse/patient-data/icons/department.svg",
                      label: "Bölüm",
                      value: deptName,
                      w: 176,
                    },
                  ].map((item) => (
                    <Box key={item.label} sx={{ height: 58, width: item.w, position: "relative" }}>
                      <Box
                        sx={{
                          position: "absolute",
                          inset: 0,
                          boxShadow: "0px 12px 60px rgba(67, 71, 77, 0.08)",
                          borderRadius: "30px",
                          bgcolor: "#fff",
                        }}
                      />
                      <Box sx={{ position: "absolute", top: 9, left: 20, display: "flex", alignItems: "center", gap: "12px", width: "calc(100% - 40px)" }}>
                        <Box component="img" src={item.icon} alt="" sx={{ width: 20, height: 20, flexShrink: 0, filter: iconBlueFilter }} />
                        <Box sx={{ width: 102, display: "flex", flexDirection: "column", alignItems: "flex-start", minWidth: 0 }}>
                          <Box sx={{ letterSpacing: "-0.01em", lineHeight: "16px" }}>{item.label}</Box>
                          <MuiTooltip title={String(item.value ?? "-")} arrow enterTouchDelay={0} leaveTouchDelay={2500}>
                            <Box
                              sx={{
                                alignSelf: "stretch",
                                width: "100%",
                                minWidth: 0,
                                fontSize: 14,
                                letterSpacing: "-0.01em",
                                lineHeight: "24px",
                                fontWeight: 500,
                                color: "#2c4364",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                              }}
                            >
                              {item.value}
                            </Box>
                          </MuiTooltip>
                        </Box>
                      </Box>
                    </Box>
                  ))}

                  {[
                    { label: "Geliş Nedeni", value: visitTypeName || diagnosis },
                    { label: "Stabilite", value: displayVitalSignStatus, badge: true as const },
                  ].map((item) => (
                    <Box
                      key={item.label}
                      sx={{
                        width: 108,
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        justifyContent: "center",
                        padding: "0px 1px",
                        boxSizing: "border-box",
                        position: "relative",
                        isolation: "isolate",
                        gap: "10px",
                      }}
                    >
                      <Box
                        sx={{
                          width: "100%",
                          height: 58,
                          boxShadow: "0px 12px 60px rgba(67, 71, 77, 0.08)",
                          borderRadius: "30px",
                          bgcolor: "#fff",
                          zIndex: 0,
                          flexShrink: 0,
                        }}
                      />
                      <Box sx={{ width: 65, position: "absolute", top: 9, left: 20.5, display: "flex", alignItems: "center", justifyContent: "center", zIndex: 2, flexShrink: 0 }}>
                        <Box sx={{ width: 65, display: "flex", flexDirection: "column", alignItems: "flex-start" }}>
                          <Box sx={{ letterSpacing: "-0.01em", lineHeight: "16px" }}>{item.label}</Box>
                          {item.badge ? (
                            <Box
                              sx={{
                                width: 65,
                                borderRadius: "8px",
                                bgcolor: stabilityBadgePalette.bg,
                                border: "1px solid",
                                borderColor: stabilityBadgePalette.border,
                                boxSizing: "border-box",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                padding: "2px 8px",
                                textAlign: "right",
                                color: stabilityBadgePalette.fg,
                                mt: 0.5,
                              }}
                            >
                              <Box sx={{ letterSpacing: "-0.02em", lineHeight: "150%" }}>{item.value}</Box>
                            </Box>
                          ) : (
                            <MuiTooltip title={String(item.value ?? "-")} arrow enterTouchDelay={0} leaveTouchDelay={2500}>
                              <Box
                                sx={{
                                  fontSize: 14,
                                  letterSpacing: "-0.01em",
                                  lineHeight: "24px",
                                  fontWeight: 500,
                                  color: "#2c4364",
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                  whiteSpace: "nowrap",
                                }}
                              >
                                {item.value}
                              </Box>
                            </MuiTooltip>
                          )}
                        </Box>
                      </Box>
                    </Box>
                  ))}
                </Box>
              </Box>

              <ByzMuiButton
                variant2="text"
                onClick={() => setMinimized(false)}
                sx={{
                  minWidth: 40,
                  height: 40,
                  p: 0,
                  backgroundColor: "transparent",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  "&:hover": { backgroundColor: "transparent", opacity: 0.8 },
                  transition: "all 0.3s ease",
                }}
              >
                <Box component="img" src="/assets/icons/vector/down.svg" alt="" sx={{ width: 40, height: 40 }} />
              </ByzMuiButton>
            </Box>
          </Box>
        </Card>
        )
      ) : useMobileLayout ? (
        <Card
          sx={{
            p: 0,
            mb: noMargin ? 0 : 2,
            borderRadius: 2,
            border: "1px solid",
            borderColor: "divider",
            overflow: "hidden",
          }}
        >
          <Box sx={{ p: 1.5, borderBottom: "1px solid #f1f5f9" }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.25 }}>
              <Box
                sx={{
                  width: 42,
                  height: 42,
                  borderRadius: "999px",
                  bgcolor: "#f1f5f9",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0,
                }}
              >
                <Box
                  component="img"
                  src={avatarIcon}
                  alt=""
                  sx={{ width: 34, height: 34, borderRadius: "999px", objectFit: "cover" }}
                />
              </Box>
              <Box sx={{ minWidth: 0, flex: 1, display: "flex", flexDirection: "column", justifyContent: "center" }}>
                <Box
                  component="b"
                  sx={{
                    display: "block",
                    color: "#0f172a",
                    fontSize: 16,
                    lineHeight: "22px",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {fullName}
                </Box>
                <Box sx={{ color: "#64748b", fontSize: 11, lineHeight: "16px", fontWeight: 600, mt: 0.25 }}>
                  {ageText} / {bloodGroup}
                </Box>
                {renderIsolationIconsRow(18, 12)}
              </Box>
            </Box>

            <Box
              sx={{
                mt: 1.25,
                display: "grid",
                gridTemplateColumns: "repeat(7, minmax(0, 1fr))",
                gap: 1,
                width: "100%",
              }}
            >
              {summaryCards.slice(0, 2).map(renderMobileInfoCard)}
              <Box
                sx={{
                  gridColumn: "span 1",
                  minHeight: 56,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <ByzMuiButton
                  variant2="text"
                  onClick={() => setMinimized(true)}
                  sx={{
                    minWidth: 28,
                    width: 28,
                    height: 28,
                    p: 0,
                    backgroundColor: "transparent",
                    "&:hover": { backgroundColor: "transparent", opacity: 0.8 },
                  }}
                >
                  <Box
                    component="img"
                    src="/assets/icons/vector/down.svg"
                    alt=""
                    sx={{ width: 24, height: 24, transform: "rotate(180deg)" }}
                  />
                </ByzMuiButton>
              </Box>
              {summaryCards.slice(2).map(renderMobileInfoCard)}
              {detailCards.map(renderMobileInfoCard)}
            </Box>
          </Box>
        </Card>
      ) : (
        <Card
          sx={{
            p: 0,
            mb: noMargin ? 0 : 2,
            borderRadius: 2,
            border: "1px solid",
            borderColor: "divider",
            overflow: "hidden",
          }}
        >
          <Box
            sx={{
              width: "100%",
              position: "relative",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: "10px",
              textAlign: "center",
              fontSize: 14,
              color: "#64748b",
              fontFamily: "Poppins",
              borderBottom: "1px solid #f1f5f9",
              padding: "10px 12px",
              boxSizing: "border-box",
            }}
          >
            <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center" }}>
              <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: "10px" }}>
                <Box component="img" src={avatarIcon} alt="" sx={{ width: 50, height: 50, borderRadius: "999px", objectFit: "cover" }} />
                <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
                  <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: "5px" }}>
                    <Box component="b" sx={{ width: 143, height: 31, fontSize: 20, lineHeight: "36px", display: "inline-block", color: "#0f172a", textAlign: "left", flexShrink: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {fullName}
                    </Box>
                    <Box sx={{ alignSelf: "stretch", lineHeight: "24px", fontWeight: 600, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{ageText} / {bloodGroup}</Box>
                    {renderIsolationIconsRow(20, 14)}
                  </Box>
                </Box>
              </Box>
            </Box>

            <Box sx={{ display: "flex", flexDirection: "column", alignItems: "flex-start", justifyContent: "center", gap: "10px", textAlign: "left", fontSize: 10, color: "#838fa0" }}>
              <Box sx={{ display: "flex", alignItems: "flex-start", gap: "10px" }}>
                {[
                  { icon: "/assets/icons/pages/nurse/patient-data/icons/order.svg", label: "Dosya - Protokol", value: protocolText, w: 176 },
                  { icon: "/assets/icons/pages/nurse/patient-data/icons/hospital-bed.svg", label: "ODA NO", value: bedInfo, w: 176 },
                  { icon: "/assets/icons/pages/nurse/patient-data/icons/department.svg", label: "Bölüm", value: deptName, w: 176 },
                ].map((item) => (
                  <Box key={item.label} sx={{ height: 58, width: item.w, position: "relative" }}>
                    <Box sx={{ position: "absolute", inset: 0, boxShadow: "0px 12px 60px rgba(67, 71, 77, 0.08)", borderRadius: "30px", bgcolor: "#fff" }} />
                    <Box sx={{ position: "absolute", top: 9, left: 20, display: "flex", alignItems: "center", gap: "12px", width: "calc(100% - 40px)" }}>
                      <Box component="img" src={item.icon} alt="" sx={{ width: 20, height: 20, flexShrink: 0, filter: iconBlueFilter }} />
                      <Box sx={{ width: 102, display: "flex", flexDirection: "column", alignItems: "flex-start", minWidth: 0 }}>
                        <Box sx={{ letterSpacing: "-0.01em", lineHeight: "16px" }}>{item.label}</Box>
                        <MuiTooltip title={String(item.value ?? "-")} arrow enterTouchDelay={0} leaveTouchDelay={2500}>
                          <Box sx={{ alignSelf: "stretch", width: "100%", minWidth: 0, fontSize: 14, letterSpacing: "-0.01em", lineHeight: "24px", fontWeight: 500, color: "#2c4364", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                            {item.value}
                          </Box>
                        </MuiTooltip>
                      </Box>
                    </Box>
                  </Box>
                ))}

                {[
                  { label: "Geliş Nedeni", value: visitTypeName || diagnosis },
                  { label: "Stabilite", value: displayVitalSignStatus, badge: true as const },
                ].map((item) => (
                  <Box key={item.label} sx={{ width: 108, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: "0px 1px", boxSizing: "border-box", position: "relative", isolation: "isolate", gap: "10px" }}>
                    <Box sx={{ width: "100%", height: 58, boxShadow: "0px 12px 60px rgba(67, 71, 77, 0.08)", borderRadius: "30px", bgcolor: "#fff", zIndex: 0, flexShrink: 0 }} />
                    <Box sx={{ width: 65, position: "absolute", top: 9, left: 20.5, display: "flex", alignItems: "center", justifyContent: "center", zIndex: 2, flexShrink: 0 }}>
                      <Box sx={{ width: 65, display: "flex", flexDirection: "column", alignItems: "flex-start" }}>
                        <Box sx={{ letterSpacing: "-0.01em", lineHeight: "16px" }}>{item.label}</Box>
                        {item.badge ? (
                          <Box sx={{ width: 65, borderRadius: "8px", bgcolor: stabilityBadgePalette.bg, border: "1px solid", borderColor: stabilityBadgePalette.border, boxSizing: "border-box", display: "flex", alignItems: "center", justifyContent: "center", padding: "2px 8px", textAlign: "right", color: stabilityBadgePalette.fg, mt: 0.5 }}>
                            <Box sx={{ letterSpacing: "-0.02em", lineHeight: "150%" }}>{item.value}</Box>
                          </Box>
                        ) : (
                          <MuiTooltip title={String(item.value ?? "-")} arrow enterTouchDelay={0} leaveTouchDelay={2500}>
                            <Box sx={{ alignSelf: "stretch", fontSize: 14, letterSpacing: "-0.01em", lineHeight: "24px", fontWeight: 500, color: "#2c4364", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                              {item.value}
                            </Box>
                          </MuiTooltip>
                        )}
                      </Box>
                    </Box>
                  </Box>
                ))}
              </Box>

              <Box sx={{ width: "100%", maxWidth: 784, display: "flex", alignItems: "flex-start", gap: "10px" }}>
                {[
                  { icon: "/assets/icons/pages/nurse/patient-data/icons/height-weight-vki.svg", label: "Boy / Kilo / VKI :", value: heightWeightBmiText, w: 176, iconSize: 20 },
                  { icon: "/assets/icons/pages/nurse/patient-data/icons/patient-clock.svg", label: "Yatış Zamanı", value: admissionDateText, w: 176, iconSize: 17 },
                  { icon: "/assets/icons/pages/nurse/patient-data/icons/doctor.svg", label: "Doktor", value: doctor, w: 176, iconSize: 20, wideText: true as const },
                  { icon: "/assets/icons/pages/nurse/patient-data/icons/diagnosis.svg", label: "Tanı", value: diagnosis, w: 226, iconSize: 20 },
                ].map((item) => (
                  <Box key={item.label} sx={{ height: 58, width: item.w, position: "relative" }}>
                    <Box sx={{ position: "absolute", inset: 0, boxShadow: "0px 12px 60px rgba(67, 71, 77, 0.08)", borderRadius: "30px", bgcolor: "#fff" }} />
                    <Box sx={{ position: "absolute", top: 9, left: 20, display: "flex", alignItems: "center", gap: "12px", width: "calc(100% - 40px)" }}>
                      <Box component="img" src={item.icon} alt="" sx={{ width: item.iconSize, height: item.iconSize, flexShrink: 0, filter: iconBlueFilter }} />
                      <Box sx={{ width: item.wideText ? 119 : 102, display: "flex", flexDirection: "column", alignItems: "flex-start", minWidth: 0 }}>
                        <Box sx={{ letterSpacing: "-0.01em", lineHeight: "16px" }}>{item.label}</Box>
                        <MuiTooltip title={String(item.value ?? "-")} arrow enterTouchDelay={0} leaveTouchDelay={2500}>
                          <Box sx={{ alignSelf: "stretch", fontSize: 14, letterSpacing: "-0.01em", lineHeight: "24px", fontWeight: 500, color: "#2c4364", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                            {item.value}
                          </Box>
                        </MuiTooltip>
                      </Box>
                    </Box>
                  </Box>
                ))}
              </Box>
            </Box>

            <ByzMuiButton
              variant2="text"
              onClick={() => setMinimized(true)}
              sx={{
                minWidth: 40,
                height: 40,
                p: 0,
                backgroundColor: "transparent",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                "&:hover": { backgroundColor: "transparent", opacity: 0.8 },
                transition: "all 0.3s ease",
              }}
            >
              <Box component="img" src="/assets/icons/vector/down.svg" alt="" sx={{ width: 40, height: 40, transform: "rotate(180deg)" }} />
            </ByzMuiButton>
          </Box>
        </Card>
      )}
      </>
      )}

      {/* Vital Bilgileri & Skorlar - tek Card içinde birleşik arka plan, açılır kapanÄ±r yapÄ± */}
      {!isCreateMedCard && (showBloodPressureChart || showDailyPulseChart || cardData?.chartScoreField) && (
        <Card
          sx={{
            p: 1.5,
            mt: onlyVitalsSection ? 0 : 2,
            borderRadius: 2,
            border: '1px solid',
            borderColor: 'divider',
          }}
        >
          {/* Vital filtre dropdown menüsü */}
          <Menu
            anchorEl={vitalFilterAnchorEl}
            open={isVitalFilterMenuOpen}
            onClose={() => setVitalFilterAnchorEl(null)}
            PaperProps={{
              sx: {
                mt: 1,
                borderRadius: 1,
                overflow: "hidden",
                boxShadow: "0px 1px 2px rgba(0, 0, 0, 0.25)",
                outline: "1px solid #E4E2E4",
                outlineOffset: "-1px",
              },
            }}
          >
            {(Object.keys(filterLabelMap) as VitalFilterOption[]).map((key) => (
              <MenuItem
                key={key}
                onClick={() => {
                  setVitalFilter(key);
                  setVitalFilterAnchorEl(null);
                }}
                sx={{
                  px: 2,
                  py: 1,
                }}
              >
                <ByzMuiTypography
                  sx={{
                    color: "#262527",
                    fontSize: 11,
                    fontFamily: "Poppins",
                    fontWeight: 500,
                    lineHeight: "20px",
                  }}
                >
                  {filterLabelMap[key]}
                </ByzMuiTypography>
              </MenuItem>
            ))}
            <Divider />
            <MenuItem
              onClick={() => setVitalFilterAnchorEl(null)}
              sx={{ px: 2, py: 1 }}
            >
              <ByzMuiTypography
                sx={{
                  color: "#2196F3",
                  fontSize: 14,
                  fontFamily: "Inter",
                  fontWeight: 500,
                  lineHeight: "20px",
                }}
              >
                Kapat
              </ByzMuiTypography>
            </MenuItem>
          </Menu>
          {/* Skor filtre dropdown menüsü */}
          <Menu
            anchorEl={scoreFilterAnchorEl}
            open={isScoreFilterMenuOpen}
            onClose={() => setScoreFilterAnchorEl(null)}
            PaperProps={{
              sx: {
                mt: 1,
                borderRadius: 1,
                overflow: "hidden",
                boxShadow: "0px 1px 2px rgba(0, 0, 0, 0.25)",
                outline: "1px solid #E4E2E4",
                outlineOffset: "-1px",
              },
            }}
          >
            {(Object.keys(filterLabelMap) as VitalFilterOption[]).map((key) => (
              <MenuItem
                key={key}
                onClick={() => {
                  setScoreFilter(key);
                  setScoreFilterAnchorEl(null);
                }}
                sx={{
                  px: 2,
                  py: 1,
                }}
              >
                <ByzMuiTypography
                  sx={{
                    color: "#262527",
                    fontSize: 11,
                    fontFamily: "Poppins",
                    fontWeight: 500,
                    lineHeight: "20px",
                  }}
                >
                  {filterLabelMap[key]}
                </ByzMuiTypography>
              </MenuItem>
            ))}
            <Divider />
            <MenuItem
              onClick={() => setScoreFilterAnchorEl(null)}
              sx={{ px: 2, py: 1 }}
            >
              <ByzMuiTypography
                sx={{
                  color: "#2196F3",
                  fontSize: 14,
                  fontFamily: "Inter",
                  fontWeight: 500,
                  lineHeight: "20px",
                }}
              >
                Kapat
              </ByzMuiTypography>
            </MenuItem>
          </Menu>

          {/* Kapalıyken görünen başlık satırı (2 kart + ok) */}
          <Box
            sx={{
              display: "grid",
              gap: 2,
              alignItems: "center",
              gridTemplateColumns: chartGridTemplateColumns,
            }}
          >
            {(showBloodPressureChart || showDailyPulseChart) && (
              <Box>
                <Box
                  sx={{
                    height: 46,
                    borderRadius: 1.5,
                    bgcolor: "white",
                    boxShadow:
                      "0px 10px 24px -8px rgba(42, 51, 70, 0.03), 0px 0px 0px 1px rgba(0, 0, 0, 0.07)",
                    px: 2,
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                  }}
                >
                  <Box
                    sx={{
                      width: 26,
                      height: 26,
                      borderRadius: "999px",
                      bgcolor: "#F5F5F5",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      flexShrink: 0,
                    }}
                  >
                    <Box component="img" src="/assets/icons/generals/users.svg" alt="" sx={{ width: 14, height: 14 }} />
                  </Box>
                  <ByzMuiTypography
                    sx={{
                      flex: "1 1 0",
                      color: "black",
                      fontSize: 16,
                      fontFamily: "Poppins",
                      fontWeight: 600,
                      lineHeight: "24px",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {resolvedVitalTitle}
                  </ByzMuiTypography>
                  <ByzMuiButton
                    variant2="text"
                    onClick={(e) => setVitalFilterAnchorEl(e.currentTarget)}
                    sx={{
                      height: 26,
                      minWidth: 0,
                      px: 1,
                      py: 0.75,
                      borderRadius: 0.75,
                      bgcolor: "white",
                      boxShadow: "0px 0px 0px 1px rgba(0, 0, 0, 0.09)",
                      "&:hover": { bgcolor: "white", opacity: 0.95 },
                    }}
                  >
                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                      <ByzMuiTypography
                        sx={{
                          color: "black",
                          fontSize: 11,
                          fontFamily: "Poppins",
                          fontWeight: 400,
                          lineHeight: "20px",
                        }}
                      >
                        {vitalFilterLabel}
                      </ByzMuiTypography>
                      <Box
                        sx={{
                          width: 0,
                          height: 0,
                          borderLeft: "5px solid transparent",
                          borderRight: "5px solid transparent",
                          borderTop: "6px solid #172A41",
                        }}
                      />
                    </Box>
                  </ByzMuiButton>
                </Box>
                {useMobileLayout && isVitalsOpen && (
                  <Box
                    sx={{
                      mt: 1.5,
                      borderRadius: 3,
                      border: "1px solid",
                      borderColor: "grey.100",
                      p: 2,
                      bgcolor: "background.paper",
                      boxShadow:
                        "0px 10px 10px rgba(15, 23, 42, 0.14), 3px 0px 3px rgb(255, 255, 255), 0px 0px 0px rgb(255, 255, 255)",
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "flex-start",
                        gap: 1.25,
                        flexWrap: "wrap",
                        mb: 1,
                      }}
                    >
                      <Box sx={{ px: 1, py: 0.5, bgcolor: "#375DFB", boxShadow: "0px 0px 4px rgba(0,0,0,0.15) inset", borderRadius: 20 }}>
                        <ByzMuiTypography sx={{ color: "white", fontSize: 11, fontFamily: "Poppins", fontWeight: 500, lineHeight: "18px", textAlign: "center", minWidth: 52 }}>
                          Nabız
                        </ByzMuiTypography>
                      </Box>
                      <Box sx={{ px: 1, py: 0.5, bgcolor: "#F0881F", boxShadow: "0px 0px 4px rgba(0,0,0,0.15) inset", borderRadius: 20, border: "1px solid #F0881F" }}>
                        <ByzMuiTypography sx={{ color: "white", fontSize: 11, fontFamily: "Poppins", fontWeight: 500, lineHeight: "18px", textAlign: "center", minWidth: 72 }}>
                          Sys (mmHg)
                        </ByzMuiTypography>
                      </Box>
                      <Box sx={{ px: 1, py: 0.5, bgcolor: "#9747B7", boxShadow: "0px 0px 4px rgba(0,0,0,0.15) inset", borderRadius: 20, border: "1px solid #9747B7" }}>
                        <ByzMuiTypography sx={{ color: "white", fontSize: 11, fontFamily: "Poppins", fontWeight: 500, lineHeight: "18px", textAlign: "center", minWidth: 72 }}>
                          Dia (mmHg)
                        </ByzMuiTypography>
                      </Box>
                      <Box sx={{ px: 1, py: 0.5, bgcolor: "#280663", boxShadow: "0px 0px 4px rgba(0,0,0,0.15) inset", borderRadius: 20 }}>
                        <ByzMuiTypography sx={{ color: "white", fontSize: 11, fontFamily: "Poppins", fontWeight: 500, lineHeight: "18px", textAlign: "center", minWidth: 52 }}>
                          SpO 2
                        </ByzMuiTypography>
                      </Box>
                    </Box>
                    <Box sx={{ width: "100%", minWidth: 1, height: 188, minHeight: 188 }}>
                      <ResponsiveContainer width="99%" height="100%" minWidth={1}>
                        <LineChart
                          data={visibleVitalChartData}
                          margin={{ left: 0, right: 0, top: 4, bottom: vitalChartBottomMargin }}
                        >
                          <CartesianGrid stroke="#E0E0E0" strokeOpacity={1} vertical horizontal />
                          <XAxis
                            dataKey="name"
                            type="category"
                            padding={{ left: 6, right: 6 }}
                            interval={0}
                            minTickGap={8}
                            angle={vitalTickAngle}
                            textAnchor="end"
                            height={vitalChartTickHeight}
                            tickMargin={2}
                            tick={{ fill: "rgba(134,140,152,0.85)", fontSize: 9 }}
                          />
                          <YAxis
                            width={28}
                            tick={{ fill: "#757575", fontSize: 10, fontFamily: "Poppins" }}
                            axisLine={{ stroke: "#E0E0E0" }}
                            tickLine={{ stroke: "#E0E0E0" }}
                            domain={[vitalMinMax.min, vitalMinMax.max]}
                            allowDecimals={false}
                          />
                          <Tooltip
                            contentStyle={{
                              backgroundColor: "rgba(255, 255, 255, 0.97)",
                              border: "1px solid #F2F2F2",
                              borderRadius: 5,
                              padding: "6px 8px",
                            }}
                            cursor={{ stroke: "#D1D5DB", strokeWidth: 1 }}
                            labelFormatter={(label) => label}
                          />
                          <Line type="monotone" dataKey="Pulse" name="Nabız" stroke="#375DFB" strokeWidth={2.5} dot={{ r: 4 }} activeDot={{ r: 6, strokeWidth: 2, stroke: "#fff", fill: "#375DFB" }} />
                          <Line type="monotone" dataKey="SYS" name="Sys (mmHg)" stroke="#F0881F" strokeWidth={2.5} dot={{ r: 4 }} activeDot={{ r: 6, strokeWidth: 2, stroke: "#fff", fill: "#F0881F" }} />
                          <Line type="monotone" dataKey="DIA" name="Dia (mmHg)" stroke="#9747B7" strokeWidth={2.5} dot={{ r: 4 }} activeDot={{ r: 6, strokeWidth: 2, stroke: "#fff", fill: "#9747B7" }} />
                          <Line type="monotone" dataKey="SpO2" name="SpO 2" stroke="#280663" strokeWidth={2.5} dot={{ r: 4 }} activeDot={{ r: 6, strokeWidth: 2, stroke: "#fff", fill: "#280663" }} />
                        </LineChart>
                      </ResponsiveContainer>
                    </Box>
                  </Box>
                )}
              </Box>
            )}

            {cardData?.chartScoreField && (
              <Box>
                <Box
                  sx={{
                    height: 46,
                    borderRadius: 1.5,
                    bgcolor: "white",
                    boxShadow:
                      "0px 10px 24px -8px rgba(42, 51, 70, 0.03), 0px 0px 0px 1px rgba(0, 0, 0, 0.07)",
                    px: 2,
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                  }}
                >
                  <Box
                    sx={{
                      width: 26,
                      height: 26,
                      borderRadius: "999px",
                      bgcolor: "#F5F5F5",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      flexShrink: 0,
                    }}
                  >
                    <Box component="img" src="/assets/icons/generals/users.svg" alt="" sx={{ width: 14, height: 14 }} />
                  </Box>
                  <ByzMuiTypography
                    sx={{
                      flex: "1 1 0",
                      color: "black",
                      fontSize: 16,
                      fontFamily: "Poppins",
                      fontWeight: 600,
                      lineHeight: "24px",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {scoresTitle}
                  </ByzMuiTypography>
                  <ByzMuiButton
                    variant2="text"
                    onClick={(e) => setScoreFilterAnchorEl(e.currentTarget)}
                    sx={{
                      height: 26,
                      minWidth: 0,
                      px: 1,
                      py: 0.75,
                      borderRadius: 0.75,
                      bgcolor: "white",
                      boxShadow: "0px 0px 0px 1px rgba(0, 0, 0, 0.09)",
                      "&:hover": { bgcolor: "white", opacity: 0.95 },
                    }}
                  >
                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                      <ByzMuiTypography
                        sx={{
                          color: "black",
                          fontSize: 11,
                          fontFamily: "Poppins",
                          fontWeight: 400,
                          lineHeight: "20px",
                        }}
                      >
                        {scoreFilterLabel}
                      </ByzMuiTypography>
                      <Box
                        sx={{
                          width: 0,
                          height: 0,
                          borderLeft: "5px solid transparent",
                          borderRight: "5px solid transparent",
                          borderTop: "6px solid #172A41",
                        }}
                      />
                    </Box>
                  </ByzMuiButton>
                </Box>
                {useMobileLayout && isVitalsOpen && (
                  <Box
                    sx={{
                      mt: 1.5,
                      borderRadius: 3,
                      border: "1px solid",
                      borderColor: "grey.100",
                      p: 2,
                      bgcolor: "background.paper",
                      boxShadow:
                        "0px 10px 10px rgba(15, 23, 42, 0.14), 3px 0px 3px rgb(255, 255, 255), 0px 0px 0px rgb(255, 255, 255)",
                    }}
                  >
                    <Box sx={{ width: "100%", minWidth: 1, mt: 0.5, height: 200, minHeight: 200 }}>
                      <ResponsiveContainer width="99%" height="100%" minWidth={1}>
                        <LineChart
                          data={scoreChartDataNormalized}
                          margin={{ left: 4, right: 4, top: 10, bottom: scoreChartBottomMargin }}
                        >
                          <CartesianGrid stroke="#D1D5DB" strokeOpacity={0.8} vertical horizontal />
                          <XAxis
                            dataKey="name"
                            type="category"
                            padding={{ left: 6, right: 6 }}
                            interval={0}
                            minTickGap={24}
                            angle={scoreTickAngle}
                            textAnchor="end"
                            height={scoreChartTickHeight}
                            tickMargin={6}
                            tick={{ fill: "rgba(134,140,152,0.85)", fontSize: 10 }}
                          />
                          <YAxis
                            tick={{ fill: "#616E7C", fontSize: 16, fontWeight: 400 }}
                            axisLine={{ stroke: "#E5E7EB" }}
                            tickLine={{ stroke: "#E5E7EB" }}
                            domain={[0, 1]}
                            ticks={[0, 0.33, 0.67, 1]}
                            tickFormatter={(normalizedValue) => {
                              const realValue =
                                scoreMinMax.min +
                                normalizedValue * (scoreMinMax.max - scoreMinMax.min);
                              return Math.round(realValue).toString();
                            }}
                            allowDecimals={false}
                          />
                          <Tooltip
                            formatter={chartTooltipFormatter}
                            labelFormatter={chartTooltipLabelFormatter}
                            contentStyle={{
                              backgroundColor: "rgba(255, 255, 255, 0.95)",
                              border: "1px solid #E5E7EB",
                              borderRadius: "8px",
                              padding: "8px 12px",
                            }}
                            cursor={{ stroke: "#D1D5DB", strokeWidth: 1 }}
                          />
                          <Line
                            type="monotone"
                            dataKey="ScoreNormalized"
                            name={t("score")}
                            stroke="#0A8FDC"
                            strokeWidth={2.5}
                            dot={false}
                            activeDot={{ r: 8, strokeWidth: 2, stroke: "#fff", fill: "#0A8FDC" }}
                          />
                        </LineChart>
                      </ResponsiveContainer>
                    </Box>
                  </Box>
                )}
              </Box>
            )}

            {!onlyVitalsSection && (
            <Box>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "flex-end",
                  alignItems: "center",
                  height: 46,
                }}
              >
                <ByzMuiButton
                  variant2="text"
                  onClick={() => setIsVitalsOpen((prev) => !prev)}
                  sx={{
                    minWidth: 40,
                    height: 40,
                    p: 0,
                    backgroundColor: "transparent",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    "&:hover": { backgroundColor: "transparent", opacity: 0.8 },
                    transition: "all 0.3s ease",
                  }}
                >
                  <Box
                    component="img"
                    src="/assets/icons/vector/down.svg"
                    alt={isVitalsOpen ? "Daralt" : "Genişlet"}
                    sx={{
                      width: 40,
                      height: 40,
                      transform: isVitalsOpen ? "rotate(180deg)" : "rotate(0deg)",
                      transition: "transform 0.2s ease",
                    }}
                  />
                </ByzMuiButton>
              </Box>
            </Box>
            )}
          </Box>

          {/* Açıkken alttaki grafikleri göster */}
          {!useMobileLayout && isVitalsOpen && (
            <Box
              sx={{
                display: "grid",
                gap: 2,
                mt: 1.5,
                gridTemplateColumns: chartGridTemplateColumns,
              }}
            >
              {/* Sol: Vital grafiği */}
              {(showBloodPressureChart || showDailyPulseChart) && (
                <Box>
                  <Box
                    sx={{
                      borderRadius: 3,
                      border: "1px solid",
                      borderColor: "grey.100",
                      p: 2,
                      bgcolor: "background.paper",
                      height: "100%",
                      boxShadow:
                        "0px 10px 10px rgba(15, 23, 42, 0.14), 3px 0px 3px rgb(255, 255, 255), 0px 0px 0px rgb(255, 255, 255)",
                    }}
                  >
                    {/* Legend butonlarÄ± */}
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "flex-start",
                        gap: 1.25,
                        flexWrap: useMobileLayout ? "wrap" : "nowrap",
                        mb: useMobileLayout ? 1 : 1.5,
                      }}
                    >
                      <Box
                        sx={{
                          px: 1,
                          py: 0.5,
                          bgcolor: "#375DFB",
                          boxShadow: "0px 0px 4px rgba(0,0,0,0.15) inset",
                          borderRadius: 20,
                        }}
                      >
                        <ByzMuiTypography
                          sx={{
                            color: "white",
                            fontSize: useMobileLayout ? 11 : 12,
                            fontFamily: "Poppins",
                            fontWeight: 500,
                            lineHeight: "18px",
                            textAlign: "center",
                            minWidth: useMobileLayout ? 52 : 64,
                          }}
                        >
                          Nabız
                        </ByzMuiTypography>
                      </Box>
                      <Box
                        sx={{
                          px: 1,
                          py: 0.5,
                          bgcolor: "#F0881F",
                          boxShadow: "0px 0px 4px rgba(0,0,0,0.15) inset",
                          borderRadius: 20,
                          border: "1px solid #F0881F",
                        }}
                      >
                        <ByzMuiTypography
                          sx={{
                            color: "white",
                            fontSize: useMobileLayout ? 11 : 12,
                            fontFamily: "Poppins",
                            fontWeight: 500,
                            lineHeight: "18px",
                            textAlign: "center",
                            minWidth: useMobileLayout ? 72 : 88,
                          }}
                        >
                          Sys (mmHg)
                        </ByzMuiTypography>
                      </Box>
                      <Box
                        sx={{
                          px: 1,
                          py: 0.5,
                          bgcolor: "#9747B7",
                          boxShadow: "0px 0px 4px rgba(0,0,0,0.15) inset",
                          borderRadius: 20,
                          border: "1px solid #9747B7",
                        }}
                      >
                        <ByzMuiTypography
                          sx={{
                            color: "white",
                            fontSize: useMobileLayout ? 11 : 12,
                            fontFamily: "Poppins",
                            fontWeight: 500,
                            lineHeight: "18px",
                            textAlign: "center",
                            minWidth: useMobileLayout ? 72 : 88,
                          }}
                        >
                          Dia (mmHg)
                        </ByzMuiTypography>
                      </Box>
                      <Box
                        sx={{
                          px: 1,
                          py: 0.5,
                          bgcolor: "#280663",
                          boxShadow: "0px 0px 4px rgba(0,0,0,0.15) inset",
                          borderRadius: 20,
                        }}
                      >
                        <ByzMuiTypography
                          sx={{
                            color: "white",
                            fontSize: useMobileLayout ? 11 : 12,
                            fontFamily: "Poppins",
                            fontWeight: 500,
                            lineHeight: "18px",
                            textAlign: "center",
                            minWidth: useMobileLayout ? 52 : 64,
                          }}
                        >
                          SpO 2
                        </ByzMuiTypography>
                      </Box>
                    </Box>

                    {/* Chart */}
                    <Box
                      sx={{ width: "100%", minWidth: useMobileLayout ? 1 : 240, height: useMobileLayout ? 188 : 200, minHeight: useMobileLayout ? 188 : 200 }}
                    >
                      <ResponsiveContainer width="99%" height="100%" minWidth={1}>
                        <LineChart
                          data={visibleVitalChartData}
                          margin={{ left: useMobileLayout ? 0 : 4, right: useMobileLayout ? 0 : 4, top: useMobileLayout ? 4 : 10, bottom: vitalChartBottomMargin }}
                        >
                          <CartesianGrid
                            stroke="#E0E0E0"
                            strokeOpacity={1}
                            vertical
                            horizontal
                          />
                          <XAxis
                            dataKey="name"
                            type="category"
                            padding={{ left: 6, right: 6 }}
                            interval={0}
                            minTickGap={useMobileLayout ? 8 : 24}
                            angle={vitalTickAngle}
                            textAnchor="end"
                            height={vitalChartTickHeight}
                            tickMargin={useMobileLayout ? 2 : 6}
                            tick={{ fill: "rgba(134,140,152,0.85)", fontSize: useMobileLayout ? 9 : 10 }}
                          />
                          <YAxis
                            width={useMobileLayout ? 28 : 36}
                            tick={{ fill: "#757575", fontSize: useMobileLayout ? 10 : 12, fontFamily: "Poppins" }}
                            axisLine={{ stroke: "#E0E0E0" }}
                            tickLine={{ stroke: "#E0E0E0" }}
                            domain={[vitalMinMax.min, vitalMinMax.max]}
                            allowDecimals={false}
                          />
                          <Tooltip
                            contentStyle={{
                              backgroundColor: "rgba(255, 255, 255, 0.97)",
                              border: "1px solid #F2F2F2",
                              borderRadius: 5,
                              padding: "6px 8px",
                            }}
                            cursor={{ stroke: "#D1D5DB", strokeWidth: 1 }}
                            labelFormatter={(label) => label}
                          />
                          {/* Nabız */}
                          <Line
                            type="monotone"
                            dataKey="Pulse"
                            name="Nabız"
                            stroke="#375DFB"
                            strokeWidth={2.5}
                            dot={{ r: 4 }}
                            activeDot={{
                              r: 6,
                              strokeWidth: 2,
                              stroke: "#fff",
                              fill: "#375DFB",
                            }}
                          />
                          {/* SYS */}
                          <Line
                            type="monotone"
                            dataKey="SYS"
                            name="Sys (mmHg)"
                            stroke="#F0881F"
                            strokeWidth={2.5}
                            dot={{ r: 4 }}
                            activeDot={{
                              r: 6,
                              strokeWidth: 2,
                              stroke: "#fff",
                              fill: "#F0881F",
                            }}
                          />
                          {/* DIA */}
                          <Line
                            type="monotone"
                            dataKey="DIA"
                            name="Dia (mmHg)"
                            stroke="#9747B7"
                            strokeWidth={2.5}
                            dot={{ r: 4 }}
                            activeDot={{
                              r: 6,
                              strokeWidth: 2,
                              stroke: "#fff",
                              fill: "#9747B7",
                            }}
                          />
                          {/* SpO2 */}
                          <Line
                            type="monotone"
                            dataKey="SpO2"
                            name="SpO 2"
                            stroke="#280663"
                            strokeWidth={2.5}
                            dot={{ r: 4 }}
                            activeDot={{
                              r: 6,
                              strokeWidth: 2,
                              stroke: "#fff",
                              fill: "#280663",
                            }}
                          />
                        </LineChart>
                      </ResponsiveContainer>
                    </Box>
                  </Box>
                </Box>
              )}

              {/* Sağ: Skor grafiği */}
              {cardData?.chartScoreField && (
                <Box>
                  <Box
                    sx={{
                      borderRadius: 3,
                      border: "1px solid",
                      borderColor: "grey.100",
                      p: 2,
                      bgcolor: "background.paper",
                      height: "100%",
                      boxShadow:
                        "0px 10px 10px rgba(15, 23, 42, 0.14), 3px 0px 3px rgb(255, 255, 255), 0px 0px 0px rgb(255, 255, 255)",
                    }}
                  >
                    <Box
                      sx={{ width: "100%", minWidth: useMobileLayout ? 1 : 240, mt: 4.5, height: 200, minHeight: 200 }}
                    >
                      <ResponsiveContainer width="99%" height="100%" minWidth={1}>
                        <LineChart
                          data={scoreChartDataNormalized}
                          margin={{ left: 4, right: 4, top: 10, bottom: scoreChartBottomMargin }}
                        >
                          <CartesianGrid
                            stroke="#D1D5DB"
                            strokeOpacity={0.8}
                            vertical
                            horizontal
                          />
                          <XAxis
                            dataKey="name"
                            type="category"
                            padding={{ left: 6, right: 6 }}
                            interval={0}
                            minTickGap={24}
                            angle={scoreTickAngle}
                            textAnchor="end"
                            height={scoreChartTickHeight}
                            tickMargin={6}
                            tick={{ fill: "rgba(134,140,152,0.85)", fontSize: 10 }}
                          />
                          <YAxis
                            tick={{ fill: "#616E7C", fontSize: 16, fontWeight: 400 }}
                            axisLine={{ stroke: "#E5E7EB" }}
                            tickLine={{ stroke: "#E5E7EB" }}
                            domain={[0, 1]}
                            ticks={[0, 0.33, 0.67, 1]}
                            tickFormatter={(normalizedValue) => {
                              const realValue =
                                scoreMinMax.min +
                                normalizedValue * (scoreMinMax.max - scoreMinMax.min);
                              return Math.round(realValue).toString();
                            }}
                            allowDecimals={false}
                          />
                          <Tooltip
                            formatter={chartTooltipFormatter}
                            labelFormatter={chartTooltipLabelFormatter}
                            contentStyle={{
                              backgroundColor: "rgba(255, 255, 255, 0.95)",
                              border: "1px solid #E5E7EB",
                              borderRadius: "8px",
                              padding: "8px 12px",
                            }}
                            cursor={{ stroke: "#D1D5DB", strokeWidth: 1 }}
                          />
                          <Line
                            type="monotone"
                            dataKey="ScoreNormalized"
                            name={t("score")}
                            stroke="#0A8FDC"
                            strokeWidth={2.5}
                            dot={false}
                            activeDot={{
                              r: 8,
                              strokeWidth: 2,
                              stroke: "#fff",
                              fill: "#0A8FDC",
                            }}
                          />
                        </LineChart>
                      </ResponsiveContainer>
                    </Box>
                  </Box>
                </Box>
              )}
              {/* Sağdaki 1 kolonluk boşluk, yukarÄ±daki ok ile hizalı */}
              {!onlyVitalsSection && <Box />}
            </Box>
          )}
        </Card>
      )}

    </>
  );
}

export default PatientDatas;

